import { Button } from '@abyss/web/ui/Button';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Flex } from '@abyss/web/ui/Flex';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalContext } from '../../context/PortalContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { capitalizeEachWord } from '../../utils/providerNames.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { EAP_OPTION_LABEL, FilterCollectionModel } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { BoardCertifiedInfoIcon } from './BoardCertifiedInfoIcon';
import { CheckBoxPopover } from './CheckBoxInfoPopover';
import { CulturalCompetencyTrainingIcon } from './CulturalCompetencyTrainingIcon';
import { PlanTypeDisabledInfoIcon } from './PlanTypeDisabledInfoIcon';

const buttonStyles: any = {
  'abyss-button-root': {
    height: '42px',
    marginTop: '20px',
    border: 'none',
    borderRadius: '24px',
  },
};

type Radio = {
  label: String;
  value: string | number | boolean;
  count?: number;
  isDisabled?: boolean;
};

type Option = {
  label: String;
  value: string | number | boolean;
  count?: number;
  isDisabled?: boolean;
  dnt_label: String;
};

interface Props {
  groupLabel?: string;
  options: Array<Radio> | Array<Option>;
  defaultValue?:
    | string[]
    | number[]
    | boolean[]
    | (string | number | boolean)[];
  disableSubmitBtn?: boolean;
  model: string;
  onApply?: (currentItem: string[]) => void;
  onOptionChange?: (any) => void;
  submitBtnName?: string;
  labelFontSize?: string;
  locationForAnalytics?: string;
}

export const FilterCheckBoxGroup = ({
  options,
  defaultValue,
  groupLabel,
  model,
  onOptionChange = () => {},
  onApply = () => {},
  submitBtnName,
  disableSubmitBtn = false,
  labelFontSize = '16px',
  locationForAnalytics,
}: Props) => {
  const { t } = useTranslation();
  const [currentItem, setCurrentItem] = useState(
    defaultValue?.map(String) || []
  );
  const [enableEapFilterUsingBHProgramFF] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
  ]);
  const { portalData } = useContext(PortalContext);
  const portalLockEAPfilter = portalData?.portalConfig?.lockEapFilter;

  useEffect(() => {
    setCurrentItem(defaultValue?.map(String) || []);
  }, [JSON.stringify(defaultValue)]);

  const onSubmit = () => {
    onApply(currentItem);
  };

  const getLabelName = (filter: string) => {
    const label = options
      .find((option) => option.value.toString() === filter)
      ?.label.concat(`_${filter}`);
    return label;
  };

  function onChangeCheckboxGroupOption(e) {
    // Prevent unchecking the last option for planType filter
    if (
      model === FilterCollectionModel.PLAN_TYPE &&
      currentItem.length === 1 &&
      e.length === 0
    ) {
      // Do not allow clearing all selections for planType
      return;
    }
    let linkName;

    if (!isEqual(currentItem, e)) {
      if (isEmpty(currentItem) && e?.length === 1) {
        linkName = `${getLabelName(e[0])}:check`;
      } else {
        let updatedOption;
        if (e?.length > currentItem?.length) {
          updatedOption = difference(e, currentItem);
          linkName = `${getLabelName(updatedOption[0])}:check`;
        }
        if (e?.length < currentItem?.length) {
          updatedOption = difference(currentItem, e);
          linkName = `${getLabelName(updatedOption[0])}:uncheck`;
        }
      }
      adobeLinkTrackEvent({
        name: linkName,
        location: locationForAnalytics,
        type: 'checkbox',
      });
    }

    setCurrentItem(e);
    onOptionChange({
      [model]: options.filter((option) => e.includes(option.value.toString())),
    });
  }

  function listOfLabels(label: String, model: string) {
    if (
      model === t('CONTENT_FILTERS.HOSPITAL_AFFILIATIONS').replace(/\s/g, '')
    ) {
      return capitalizeEachWord(label);
    } else {
      return label;
    }
  }

  return (
    <React.Fragment>
      <Flex
        css={{
          'abyss-flex-root': {
            maxHeight: '314px',
            overflow: 'auto',
            overflowX: 'hidden',
            paddingLeft: '4px',
            paddingBottom: '4px',
          },
        }}
        direction="column"
      >
        <CheckboxGroup
          css={{
            'abyss-checkbox-group-label': {
              margin: '0',
            },
            'abyss-checkbox-group-root': {
              border: 'none',
            },
          }}
          data-auto-testid={`${model}-group`}
          data-testid={`${model}-group`}
          label={groupLabel}
          onChange={(e) => {
            onChangeCheckboxGroupOption(e);
          }}
          validators={{ required: false }}
          value={currentItem}
        >
          {options.map(({ value, label, count, isDisabled, dnt_label }) => {
            const shouldBeDisabled =
              count === 0 ||
              isDisabled ||
              (portalLockEAPfilter && dnt_label === EAP_OPTION_LABEL);
            return (
              <Checkbox
                css={{
                  'abyss-checkbox-root': {
                    padding: '12px 0px 12px $xs',
                    margin: '0 !important',
                    alignItems: 'center !important',
                    display:
                      model === FilterCollectionModel.AREA_OF_EXPERTISE &&
                      dnt_label === EAP_OPTION_LABEL &&
                      enableEapFilterUsingBHProgramFF
                        ? 'none'
                        : 'initial',
                  },
                  'abyss-checkbox-label': {
                    fontSize: labelFontSize,
                    color: shouldBeDisabled
                      ? '$gray5'
                      : !shouldBeDisabled &&
                        (currentItem?.includes(value.toString())
                          ? '$gray8'
                          : '$gray7'),
                    fontWeight:
                      !shouldBeDisabled &&
                      (currentItem?.includes(value.toString())
                        ? '$bold'
                        : '$medium'),
                  },
                }}
                data-testid="checkbox-selection"
                isDisabled={shouldBeDisabled}
                key={label.concat(`_${value}`)}
                label={
                  count || count === 0
                    ? `${label} (${count})`
                    : listOfLabels(label, model)
                }
                value={value.toString()}
              />
            );
          })}
        </CheckboxGroup>

        {model === FilterCollectionModel.PLAN_TYPE &&
          options.some((o) => o.isDisabled) && <PlanTypeDisabledInfoIcon />}
        {model ===
        FilterCollectionModel.BOARD_CERTIFIED_SPECIALITY_INDICATOR ? (
          <BoardCertifiedInfoIcon />
        ) : null}
      </Flex>
      {model === FilterCollectionModel.CULTURAL_COMPETENCE ? (
        <CulturalCompetencyTrainingIcon />
      ) : null}
      {(model === FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR ||
        model === FilterCollectionModel.PRIORITY_SCHEDULING) && (
        <CheckBoxPopover filterModel={model} />
      )}
      {submitBtnName ? (
        <Button
          css={buttonStyles}
          data-auto-testid="apply-button"
          data-testid="apply-button"
          isDisabled={disableSubmitBtn}
          onClick={onSubmit}
        >
          {submitBtnName}
        </Button>
      ) : null}
    </React.Fragment>
  );
};
